import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";

const SUGGESTIONS = [
  "Hvad kan Aicom AI hjælpe mig med?",
  "Hvilket modul skal jeg starte med?",
  "Hvad er forskellen på planerne?",
  "Hvordan får jeg bedre indhold?",
];

export default function AssistantWidget() {
  const { user } = useAuth();
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setMessages([]);
    setLoaded(false);
    setOpen(false);
  }, [user?.id]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => {
    if (open && !loaded) {
      api.get("/assistant/history")
        .then(({ data }) => setMessages(data.messages || []))
        .catch(() => {})
        .finally(() => { setLoaded(true); scrollToBottom(); });
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 250);
  }, [open, loaded, scrollToBottom]);

  useEffect(() => { scrollToBottom(); }, [messages, sending, scrollToBottom]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(apiError(err));
      setMessages((m) => [...m, { role: "assistant", content: t("Beklager – jeg kunne ikke svare lige nu. Prøv igen om lidt.") }]);
    } finally {
      setSending(false);
    }
  };

  const clearChat = async () => {
    try {
      await api.delete("/assistant/history");
      setMessages([]);
      toast.success(t("Samtalen er ryddet"));
    } catch (err) { toast.error(apiError(err)); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        data-testid="assistant-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Åbn Aicom-hjælperen"
        className="fixed bottom-20 right-5 z-[9999] h-14 w-14 rounded-full bg-foreground text-background flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-transform duration-200"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X className="h-6 w-6" strokeWidth={2} />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <MessageCircle className="h-6 w-6" strokeWidth={2} />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="assistant-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="fixed bottom-40 right-5 z-[9999] w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-11rem)] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-foreground flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-background" strokeWidth={2} />
                </div>
                <div className="leading-tight">
                  <p className="text-sm font-medium text-foreground">{t("Aicom-hjælperen")}</p>
                  <p className="text-xs text-muted-foreground">{t("Spørg om alt på platformen")}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <button data-testid="assistant-clear" onClick={clearChat} title={t("Ryd samtale")}
                    className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
                <button data-testid="assistant-close" onClick={() => setOpen(false)} title={t("Luk")}
                  className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center pt-6">
                  <div className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="h-6 w-6 text-foreground" strokeWidth={1.75} />
                  </div>
                  <p className="text-sm text-foreground font-medium">{t("Hej! Hvad kan jeg hjælpe med?")}</p>
                  <p className="text-xs text-muted-foreground mt-1 mb-4">{t("Jeg kender platformen og jeres virksomhed.")}</p>
                  <div className="flex flex-col gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid="assistant-suggestion" onClick={() => send(s)}
                        className="text-left text-sm px-3 py-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/50 transition-colors">
                        {t(s)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`assistant-msg-${m.role}`}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                      m.role === "user"
                        ? "bg-foreground text-background rounded-br-sm"
                        : "bg-secondary text-foreground rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {sending && (
                <div className="flex justify-start" data-testid="assistant-typing">
                  <div className="bg-secondary text-muted-foreground rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">{t("Skriver...")}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-border p-3 shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  data-testid="assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  rows={1}
                  placeholder={t("Skriv dit spørgsmål...")}
                  className="flex-1 resize-none max-h-28 rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <button
                  data-testid="assistant-send"
                  onClick={() => send()}
                  disabled={sending || !input.trim()}
                  className="h-10 w-10 shrink-0 rounded-xl bg-foreground text-background flex items-center justify-center disabled:opacity-40 hover:opacity-90 transition-opacity"
                >
                  <Send className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
              <p className="mt-2 text-center text-[11px] text-muted-foreground">
                {t("Fik du ikke svar, som du ønskede?")}{" "}
                <a href="mailto:hello@aicom.dk" data-testid="assistant-email-link" className="text-foreground underline underline-offset-2 hover:no-underline">hello@aicom.dk</a>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
